import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Grid from "../components/Grid";
import { FaEye, FaDownload } from "react-icons/fa";
import { DataGrid } from "@mui/x-data-grid";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function PurchaseOrderSettlements({ onAddSettlementClick }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [settlements, setSettlements] = useState([]);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [selectedSearchField, setSelectedSearchField] = useState("settlement_id");

  useEffect(() => {
    const fetchSettlements = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/purchaseSettlements/getSettlements`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data) {
          const formatted = response.data.map((s) => ({
            id: s.settlement_id,
            settlement_id: s.settlement_id,
            settlementDate: s.settlementDate,
            settledAmount: s.settledAmount,
            createdAt: s.createdAt,
            invoice_id: s.invoices?.[0]?.invoice_id?.invoice_code || "N/A",
            invoice_balance: s.invoices?.[0]?.invoice_id?.balance || 0,
            supplier_id: s.supplier_id?.supplier_code || "N/A",
            supplier_name: s.supplier_id?.supplier_name || "N/A",
            createdBy: s.createdBy?.username || "Unknown",
          }));
          setSettlements(formatted);
        } else {
          toast.error("No purchase order settlements found.");
        }
      } catch (err) {
        console.error(err);
        toast.error("Error fetching settlements.");
      }
    };
    fetchSettlements();
  }, []);

  const handleDownloadExcel = async () => {
    const data = settlements.filter((s) =>
      s[selectedSearchField]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (data.length === 0) {
      toast.warn("No data available to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("PO Settlements");

    const headers = [
      "Settlement ID",
      "Settlement Date",
      "Invoice ID",
      "Supplier ID",
      "Supplier Name",
      "Settled Amount (LKR)",
      "Invoice Balance (LKR)",
      "Created By",
    ];
    sheet.addRow(headers);

    sheet.getRow(1).eachCell((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD966" } };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
    });

    data.forEach((s) => {
      sheet.addRow([
        s.settlement_id,
        new Date(s.settlementDate).toLocaleDateString("en-GB"),
        s.invoice_id,
        s.supplier_id,
        s.supplier_name,
        s.settledAmount,
        s.invoice_balance,
        s.createdBy,
      ]);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      "PurchaseOrderSettlements.xlsx"
    );
  };

  const fetchSettlementDetails = async (settlement_id) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${API_BASE_URL}/purchaseSettlements/getSettlementById/${settlement_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedSettlement(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch settlement details.");
    }
  };

  const columns = [
    { field: "settlement_id", headerName: "Settlement ID", flex: 1 },
    {
      field: "settlementDate",
      headerName: "Settlement Date",
      flex: 1,
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleDateString("en-GB") : "N/A",
    },
    { field: "invoice_id", headerName: "Invoice ID", flex: 1 },
    { field: "supplier_id", headerName: "Supplier ID", flex: 1 },
    { field: "supplier_name", headerName: "Supplier Name", flex: 1 },
    {
      field: "settledAmount",
      headerName: "Settled Amount (LKR)",
      flex: 1,
      headerAlign: "right",
      align: "right",
      renderCell: (params) =>
        `${new Intl.NumberFormat("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(params.value)}`,
    },
    {
      field: "invoice_balance",
      headerName: "Invoice Balance (LKR)", // ✅ New Column
      flex: 1,
      headerAlign: "right",
      align: "right",
      renderCell: (params) =>
        `${new Intl.NumberFormat("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(params.value || 0)}`,
    },
    { field: "createdBy", headerName: "Created By", flex: 1 },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      headerAlign: "center",
      renderCell: (params) => (
        <div className="flex justify-center items-center p-3">
          <button
            onClick={() => fetchSettlementDetails(params.id)}
            className="bg-[#FBC02D] text-white p-2 rounded-md"
          >
            <FaEye />
          </button>
        </div>
      ),
    },
  ];

  const filtered = settlements.filter((s) =>
    s[selectedSearchField]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex h-screen">
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-700">
            Purchase Order Settlements
          </h1>
          <button
            onClick={onAddSettlementClick}
            className="bg-green-700 text-white px-4 py-2 rounded-lg"
          >
            Add PO Settlement
          </button>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="settlement_id">Settlement ID</option>
                <option value="settlementDate">Date</option>
                <option value="invoice_id">Invoice ID</option>
                <option value="supplier_id">Supplier ID</option>
                <option value="supplier_name">Supplier Name</option>
                <option value="createdBy">Created By</option>
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search settlements"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            <button
              onClick={handleDownloadExcel}
              className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              <FaDownload className="mr-2" />
              Download Excel
            </button>
          </div>

          <Grid rows={filtered} columns={columns} getRowId={(row) => row.settlement_id} />

          {selectedSettlement && (
            <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50 overflow-y-auto">
              <div className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative max-h-[90vh] overflow-y-auto">
                <button
                  onClick={() => setSelectedSettlement(null)}
                  className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-lg hover:bg-red-700"
                >
                  &times;
                </button>
                <h2 className="text-3xl mb-4 font-bold">Settlement Details</h2>

                <div className="space-y-3">
                  <Detail label="Settlement ID" value={selectedSettlement.settlement_id} />
                  <Detail label="Settled Amount" value={selectedSettlement.settledAmount} />
                  <Detail
                    label="Settlement Date"
                    value={selectedSettlement.settlementDate?.slice(0, 10)}
                  />
                  <Detail
                    label="Invoice ID"
                    value={selectedSettlement.invoices?.[0]?.invoice_id?.invoice_code}
                  />
                  <Detail
                    label="Invoice Amount"
                    value={selectedSettlement.invoices?.[0]?.invoice_id?.amount}
                  />
                  <Detail
                    label="Invoice Balance"
                    value={selectedSettlement.invoices?.[0]?.invoice_id?.balance}
                  />
                  <Detail
                    label="Supplier Name"
                    value={selectedSettlement.supplier_id?.supplier_name}
                  />
                </div>

                {/* Payments & Debit Notes Section */}
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Payment Receipts</h3>
                    <DataGrid
                      rows={(selectedSettlement.payment_receipts || []).map((p, i) => ({
                        id: i + 1,
                        receiptId: p.payment_receipt_id?.supplier_payment_id,
                        amount_applied: p.amount_applied,
                      }))}
                      columns={[
                        { field: "receiptId", headerName: "Receipt ID", flex: 1 },
                        {
                          field: "amount_applied",
                          headerName: "Applied Amount (LKR)",
                          flex: 1,
                          headerAlign: "right",
                          align: "right",
                        },
                      ]}
                      pageSize={10}
                      autoHeight
                    />
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-2">Debit Notes</h3>
                    <DataGrid
                      rows={(selectedSettlement.debit_notes || []).map((n, i) => ({
                        id: i + 1,
                        noteId: n.debit_note_id?.debit_note_code,
                        amount_applied: n.debited_amount,
                      }))}
                      columns={[
                        { field: "noteId", headerName: "Debit Note ID", flex: 1 },
                        {
                          field: "amount_applied",
                          headerName: "Debited Amount (LKR)",
                          flex: 1,
                          headerAlign: "right",
                          align: "right",
                        },
                      ]}
                      pageSize={10}
                      autoHeight
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Detail = ({ label, value }) => (
  <div>
    <label className="block font-semibold">{label}</label>
    <input
      type="text"
      value={value || ""}
      readOnly
      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
    />
  </div>
);

export default PurchaseOrderSettlements;
